import React, { forwardRef } from 'react';
import Icon from './Icon';
import Spinner from './Spinner';

/**
 * Prominent search + action box used on analysis pages.
 *
 * <SearchBox
 *    icon="search"
 *    placeholder="example.com"
 *    value={input}
 *    onChange={setInput}
 *    onSubmit={runAnalyze}
 *    loading={state === 'loading'}
 *    actionLabel="Analyze"
 *    rightSlot={<HashTypeBadge/>}
 * />
 */
const SearchBox = forwardRef(function SearchBox({
  icon = 'search',
  placeholder,
  value,
  onChange,
  onSubmit,
  loading = false,
  actionLabel = 'Analyze',
  loadingLabel,
  disabled = false,
  mono = false,
  rightSlot,
  invalid = false,
  style,
  inputStyle,
  ...rest
}, ref) {
  return (
    <div
      style={{
        display: 'flex', alignItems: 'center', gap: 'var(--sp-3)',
        background: 'var(--bg-surface)',
        border: `1px solid ${invalid ? 'rgba(239,68,68,0.5)' : 'var(--border)'}`,
        borderRadius: 'var(--radius-lg)',
        padding: '10px 16px',
        transition: 'border-color var(--dur-fast) var(--ease-out), box-shadow var(--dur-fast) var(--ease-out)',
        ...style,
      }}
      {...rest}
    >
      <Icon name={icon} size="md" color="var(--text-muted)" strokeWidth={2}/>
      <input
        ref={ref}
        type="text"
        autoFocus
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') onSubmit?.(); }}
        style={{
          flex: 1, background: 'none', border: 'none', outline: 'none',
          color: 'var(--text-primary)',
          fontSize: 'var(--fs-lg)',
          fontFamily: mono ? 'var(--font-mono)' : 'inherit',
          letterSpacing: mono ? '0.02em' : 'normal',
          ...inputStyle,
        }}
        spellCheck={mono ? false : undefined}
      />
      {rightSlot}
      <button
        type="button"
        onClick={onSubmit}
        disabled={disabled || loading}
        className="btn btn-primary"
        style={{
          padding: '7px 18px', fontSize: 'var(--fs-md)', fontWeight: 'var(--fw-semi)',
          opacity: (disabled || loading) ? 0.5 : 1,
          cursor: (disabled || loading) ? 'not-allowed' : 'pointer',
          whiteSpace: 'nowrap',
        }}
      >
        {loading
          ? <><Spinner size={13} thickness={2} color="#fff"/> {loadingLabel || 'Analyzing…'}</>
          : actionLabel}
      </button>
    </div>
  );
});

export default SearchBox;
